// PowerPoint.js wrappers used by AI tool calls. Dia-nummers zijn overal 1-based
// richting het model; intern 0-based via getItemAt(n - 1).
//
// ponytail: geen undo-stack zoals de Excel add-in — PowerPoint heeft geen goedkoop
// snapshot-primitief voor shapes. Toevoegen (b.v. via slide.exportAsBase64) als
// gebruikers erom vragen.

// Payload-caps: lange teksten blazen anders de model-context op.
const MAX_SHAPE_TEXT = 4000;

function trunc(t: string, n = MAX_SHAPE_TEXT): string {
  return t.length > n ? t.slice(0, n) + "…" : t;
}

// ───── Lezen ─────

export interface SlideShape {
  id: string;
  name: string;
  type: string;
  left: number;
  top: number;
  width: number;
  height: number;
  text?: string;
}

export interface SlidePayload {
  slide: number; // 1-based
  id: string;
  layout?: string;
  shapes: SlideShape[];
}

// Gedocumenteerd 3-sync patroon: shapes laden → hasText per shape → text voor
// shapes die tekst hebben (textRange laden op tekstloze shapes kan falen).
async function loadShapesWithText(
  ctx: PowerPoint.RequestContext,
  shapes: PowerPoint.ShapeCollection,
): Promise<SlideShape[]> {
  shapes.load("items/id,items/name,items/type,items/left,items/top,items/width,items/height");
  await ctx.sync();
  for (const s of shapes.items) s.textFrame.load("hasText");
  await ctx.sync();
  const withText = shapes.items.filter((s) => s.textFrame.hasText);
  for (const s of withText) s.textFrame.textRange.load("text");
  await ctx.sync();
  return shapes.items.map((s) => ({
    id: s.id,
    name: s.name,
    type: String(s.type),
    left: Math.round(s.left),
    top: Math.round(s.top),
    width: Math.round(s.width),
    height: Math.round(s.height),
    ...(s.textFrame.hasText ? { text: trunc(s.textFrame.textRange.text) } : {}),
  }));
}

export async function readSlide(slideNumber: number): Promise<SlidePayload> {
  return PowerPoint.run(async (ctx) => {
    const slide = ctx.presentation.slides.getItemAt(slideNumber - 1);
    slide.load("id");
    const layout = slide.layout;
    layout.load("name");
    const shapes = await loadShapesWithText(ctx, slide.shapes);
    return { slide: slideNumber, id: slide.id, layout: layout.name, shapes };
  });
}

// ponytail: detail van max 50 dia's; read_slide voor de rest.
const MAX_INFO_SLIDES = 50;

export async function getPresentationInfo(): Promise<{
  title: string;
  slideCount: number;
  layouts: string[];
  slides: { slide: number; id: string; shapeCount: number; texts: string[] }[];
  note?: string;
}> {
  return PowerPoint.run(async (ctx) => {
    const pres = ctx.presentation;
    pres.load("title");
    const slides = pres.slides;
    slides.load("items/id");
    const masters = pres.slideMasters;
    masters.load("items/id");
    await ctx.sync();

    const layoutCols = masters.items.map((m) => {
      const l = m.layouts;
      l.load("items/name");
      return l;
    });
    const detail = slides.items.slice(0, MAX_INFO_SLIDES);
    const shapeCols = detail.map((s) => {
      const sh = s.shapes;
      sh.load("items/id");
      return sh;
    });
    await ctx.sync();

    for (const sh of shapeCols) for (const s of sh.items) s.textFrame.load("hasText");
    await ctx.sync();
    // Per dia de eerste 5 teksten als peek — genoeg voor een overzicht.
    const textShapes = shapeCols.map((sh) => sh.items.filter((s) => s.textFrame.hasText).slice(0, 5));
    for (const arr of textShapes) for (const s of arr) s.textFrame.textRange.load("text");
    await ctx.sync();

    return {
      title: pres.title,
      slideCount: slides.items.length,
      layouts: [...new Set(layoutCols.flatMap((l) => l.items.map((i) => i.name)))],
      slides: detail.map((s, i) => ({
        slide: i + 1,
        id: s.id,
        shapeCount: shapeCols[i].items.length,
        texts: textShapes[i].map((t) => trunc(t.textFrame.textRange.text, 120)),
      })),
      ...(slides.items.length > MAX_INFO_SLIDES
        ? { note: `Alleen de eerste ${MAX_INFO_SLIDES} van ${slides.items.length} dia's; gebruik read_slide voor de rest.` }
        : {}),
    };
  });
}

const THEME_COLORS = [
  "Dark1", "Light1", "Dark2", "Light2",
  "Accent1", "Accent2", "Accent3", "Accent4", "Accent5", "Accent6",
  "Hyperlink", "FollowedHyperlink",
] as const;

export async function getPresentationTheme(): Promise<{
  colors: Record<string, string>;
  sampledFonts: { name: string; count: number; sizes: number[] }[];
  note?: string;
}> {
  return PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();
    if (!slides.items.length) return { colors: {}, sampledFonts: [], note: "Presentatie bevat geen dia's." };

    const scheme = slides.items[0].themeColorScheme;
    const colorResults = THEME_COLORS.map((color) => scheme.getThemeColor(color));

    // PowerPoint.js exposes theme colors but not the theme font scheme. Sample
    // actual text styles so the model still follows the presentation typography.
    const shapeCollections = slides.items.slice(0, 20).map((slide) => {
      slide.shapes.load("items/id");
      return slide.shapes;
    });
    await ctx.sync();
    const shapes = shapeCollections.flatMap((collection) => collection.items).slice(0, 150);
    for (const shape of shapes) shape.textFrame.load("hasText");
    await ctx.sync();
    const textShapes = shapes.filter((shape) => shape.textFrame.hasText);
    for (const shape of textShapes) shape.textFrame.textRange.font.load("name,size");
    await ctx.sync();

    const fonts = new Map<string, { count: number; sizes: Set<number> }>();
    for (const shape of textShapes) {
      const font = shape.textFrame.textRange.font;
      const name = font.name || "Mixed/Theme";
      const current = fonts.get(name) ?? { count: 0, sizes: new Set<number>() };
      current.count += 1;
      if (typeof font.size === "number") current.sizes.add(font.size);
      fonts.set(name, current);
    }

    return {
      colors: Object.fromEntries(THEME_COLORS.map((color, index) => [color, colorResults[index].value])),
      sampledFonts: [...fonts.entries()]
        .map(([name, value]) => ({ name, count: value.count, sizes: [...value.sizes].sort((a, b) => a - b) }))
        .sort((a, b) => b.count - a.count),
      note: "PowerPoint.js exposeert themakleuren direct; lettertypen zijn bemonsterd uit bestaande tekst omdat het thema-fontschema niet beschikbaar is.",
    };
  });
}

// ───── Selectie ─────

export interface SelectionPayload {
  slides: { slide: number; id: string }[];
  shapes: { id: string; name: string; text?: string }[];
  text?: string;
}

export async function readSelection(): Promise<SelectionPayload> {
  const base = await PowerPoint.run(async (ctx) => {
    const all = ctx.presentation.slides;
    all.load("items/id");
    const selSlides = ctx.presentation.getSelectedSlides();
    selSlides.load("items/id");
    const selShapes = ctx.presentation.getSelectedShapes();
    selShapes.load("items/id,items/name");
    await ctx.sync();
    for (const s of selShapes.items) s.textFrame.load("hasText");
    await ctx.sync();
    const withText = selShapes.items.filter((s) => s.textFrame.hasText);
    for (const s of withText) s.textFrame.textRange.load("text");
    await ctx.sync();
    const indexOf = new Map(all.items.map((s, i) => [s.id, i + 1]));
    return {
      slides: selSlides.items.map((s) => ({ slide: indexOf.get(s.id) ?? -1, id: s.id })),
      shapes: selShapes.items.map((s) => ({
        id: s.id,
        name: s.name,
        ...(s.textFrame.hasText ? { text: trunc(s.textFrame.textRange.text, 2000) } : {}),
      })),
    };
  });
  // Aparte run: getSelectedTextRange gooit een error als er geen tekstselectie is.
  try {
    const text = await PowerPoint.run(async (ctx) => {
      const tr = ctx.presentation.getSelectedTextRange();
      tr.load("text");
      await ctx.sync();
      return tr.text;
    });
    if (text) return { ...base, text: trunc(text, 2000) };
  } catch {
    /* geen tekstselectie */
  }
  return base;
}

export async function selectSlide(slideNumber: number): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const slide = ctx.presentation.slides.getItemAt(slideNumber - 1);
    slide.load("id");
    await ctx.sync();
    ctx.presentation.setSelectedSlides([slide.id]);
    await ctx.sync();
    return { ok: true as const };
  });
}

// ───── Dia's ─────

export async function addSlide(args: { layout?: string } = {}): Promise<{ slide: number; layout?: string }> {
  return PowerPoint.run(async (ctx) => {
    let options: PowerPoint.AddSlideOptions | undefined;
    let layoutName: string | undefined;
    if (args.layout) {
      const masters = ctx.presentation.slideMasters;
      masters.load("items/id");
      await ctx.sync();
      const layoutCols = masters.items.map((m) => {
        const l = m.layouts;
        l.load("items/id,items/name");
        return { master: m, layouts: l };
      });
      await ctx.sync();
      const needle = args.layout.toLowerCase();
      for (const { master, layouts } of layoutCols) {
        const hit = layouts.items.find((l) => l.name.toLowerCase().includes(needle));
        if (hit) {
          options = { layoutId: hit.id, slideMasterId: master.id };
          layoutName = hit.name;
          break;
        }
      }
      if (!options) throw new Error(`Layout "${args.layout}" niet gevonden. Gebruik get_presentation_info voor de beschikbare layouts.`);
    }
    ctx.presentation.slides.add(options);
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();
    // slides.add voegt achteraan toe → nieuwe dia = laatste index.
    return { slide: slides.items.length, ...(layoutName ? { layout: layoutName } : {}) };
  });
}

export async function deleteSlide(slideNumber: number): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    ctx.presentation.slides.getItemAt(slideNumber - 1).delete();
    await ctx.sync();
    return { ok: true as const };
  });
}

// ───── Shapes ─────

// Zoek shape op id, of anders op naam (case-insensitive).
async function findShape(
  ctx: PowerPoint.RequestContext,
  slideNumber: number,
  ref: string,
): Promise<PowerPoint.Shape> {
  const shapes = ctx.presentation.slides.getItemAt(slideNumber - 1).shapes;
  shapes.load("items/id,items/name");
  await ctx.sync();
  const found =
    shapes.items.find((s) => s.id === ref) ??
    shapes.items.find((s) => s.name.toLowerCase() === ref.toLowerCase());
  if (!found) {
    throw new Error(`Shape "${ref}" niet gevonden op dia ${slideNumber}. Gebruik read_slide voor de shape-ids.`);
  }
  return found;
}

// Office.js geeft de diagrootte niet; defaults gaan uit van 16:9 = 960×540 pt.
export async function addTextBox(args: {
  slide: number;
  text: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  font_size?: number;
  bold?: boolean;
  color?: string;
  align?: "Left" | "Center" | "Right" | "Justify";
}): Promise<{ slide: number; shapeId: string }> {
  return PowerPoint.run(async (ctx) => {
    const slide = ctx.presentation.slides.getItemAt(args.slide - 1);
    const shape = slide.shapes.addTextBox(args.text, {
      left: args.left ?? 60,
      top: args.top ?? 60,
      width: args.width ?? 840,
      height: args.height ?? 80,
    });
    const font = shape.textFrame.textRange.font;
    if (args.font_size !== undefined) font.size = args.font_size;
    if (args.bold !== undefined) font.bold = args.bold;
    if (args.color) font.color = args.color;
    if (args.align) {
      shape.textFrame.textRange.paragraphFormat.horizontalAlignment =
        args.align as PowerPoint.ParagraphHorizontalAlignment;
    }
    shape.load("id");
    await ctx.sync();
    return { slide: args.slide, shapeId: shape.id };
  });
}

// Veelgebruikte aliassen → PowerPoint.GeometricShapeType; onbekende types gaan
// raw door (API-error komt dan netjes terug bij het model).
const SHAPE_ALIAS: Record<string, string> = {
  square: "rectangle",
  circle: "ellipse",
  oval: "ellipse",
  rounded_rectangle: "roundRectangle",
  roundedrectangle: "roundRectangle",
  arrow: "rightArrow",
};

export async function addShape(args: {
  slide: number;
  shape_type: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  fill_color?: string;
  text?: string;
}): Promise<{ slide: number; shapeId: string; type: string }> {
  return PowerPoint.run(async (ctx) => {
    const slide = ctx.presentation.slides.getItemAt(args.slide - 1);
    const type = (SHAPE_ALIAS[args.shape_type.toLowerCase()] ?? args.shape_type) as PowerPoint.GeometricShapeType;
    const shape = slide.shapes.addGeometricShape(type, {
      left: args.left ?? 100,
      top: args.top ?? 100,
      width: args.width ?? 200,
      height: args.height ?? 120,
    });
    if (args.fill_color) shape.fill.setSolidColor(args.fill_color);
    if (args.text) shape.textFrame.textRange.text = args.text;
    shape.load("id");
    await ctx.sync();
    return { slide: args.slide, shapeId: shape.id, type: String(type) };
  });
}

export async function setShapeText(args: {
  slide: number;
  shape: string; // id of naam
  text: string;
}): Promise<{ slide: number; shapeId: string }> {
  return PowerPoint.run(async (ctx) => {
    const shape = await findShape(ctx, args.slide, args.shape);
    shape.textFrame.textRange.text = args.text;
    await ctx.sync();
    return { slide: args.slide, shapeId: shape.id };
  });
}

export interface ShapeFormat {
  fill_color?: string | null; // null → vulling wissen
  font?: { size?: number; color?: string; bold?: boolean; italic?: boolean; name?: string };
  align?: "Left" | "Center" | "Right" | "Justify";
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

export async function formatShape(args: {
  slide: number;
  shape: string;
  format: ShapeFormat;
}): Promise<{ slide: number; shapeId: string }> {
  return PowerPoint.run(async (ctx) => {
    const shape = await findShape(ctx, args.slide, args.shape);
    const f = args.format;
    if (f.fill_color === null) shape.fill.clear();
    else if (f.fill_color) shape.fill.setSolidColor(f.fill_color);
    if (f.font) {
      const font = shape.textFrame.textRange.font;
      if (f.font.size !== undefined) font.size = f.font.size;
      if (f.font.color !== undefined) font.color = f.font.color;
      if (f.font.bold !== undefined) font.bold = f.font.bold;
      if (f.font.italic !== undefined) font.italic = f.font.italic;
      if (f.font.name !== undefined) font.name = f.font.name;
    }
    if (f.align) {
      shape.textFrame.textRange.paragraphFormat.horizontalAlignment =
        f.align as PowerPoint.ParagraphHorizontalAlignment;
    }
    if (f.left !== undefined) shape.left = f.left;
    if (f.top !== undefined) shape.top = f.top;
    if (f.width !== undefined) shape.width = f.width;
    if (f.height !== undefined) shape.height = f.height;
    await ctx.sync();
    return { slide: args.slide, shapeId: shape.id };
  });
}

export async function deleteShape(args: { slide: number; shape: string }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const shape = await findShape(ctx, args.slide, args.shape);
    shape.delete();
    await ctx.sync();
    return { ok: true as const };
  });
}

// ───── Uitgebreide presentatiebesturing ─────

export function getPowerPointCapabilities(): Record<string, boolean> {
  const versions = ["1.2", "1.3", "1.4", "1.5", "1.6", "1.7", "1.8", "1.9", "1.10"];
  return Object.fromEntries(
    versions.map((version) => [
      `PowerPointApi_${version.replace(".", "_")}`,
      Office.context.requirements.isSetSupported("PowerPointApi", version),
    ]),
  );
}

async function findLayout(
  ctx: PowerPoint.RequestContext,
  name: string,
): Promise<PowerPoint.SlideLayout> {
  const masters = ctx.presentation.slideMasters;
  masters.load("items/id");
  await ctx.sync();
  const collections = masters.items.map((master) => {
    master.layouts.load("items/id,items/name");
    return master.layouts;
  });
  await ctx.sync();
  const needle = name.toLowerCase();
  const layouts = collections.flatMap((collection) => collection.items);
  const layout = layouts.find((item) => item.name.toLowerCase() === needle) ??
    layouts.find((item) => item.name.toLowerCase().includes(needle));
  if (!layout) throw new Error(`Layout "${name}" niet gevonden.`);
  return layout;
}

export async function moveSlide(args: { slide: number; to: number }): Promise<{ slide: number }> {
  return PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();
    if (args.to < 1 || args.to > slides.items.length) throw new Error("Ongeldige doelpositie.");
    slides.getItemAt(args.slide - 1).moveTo(args.to - 1);
    await ctx.sync();
    return { slide: args.to };
  });
}

export async function duplicateSlide(args: { slide: number }): Promise<{ slide: number }> {
  return PowerPoint.run(async (ctx) => {
    const slides = ctx.presentation.slides;
    slides.load("items/id");
    await ctx.sync();
    const source = slides.getItemAt(args.slide - 1);
    const exported = source.exportAsBase64();
    await ctx.sync();
    ctx.presentation.insertSlidesFromBase64(exported.value, {
      formatting: "KeepSourceFormatting",
      targetSlideId: source.id,
    });
    await ctx.sync();
    return { slide: args.slide + 1 };
  });
}

export async function applySlideLayout(args: { slide: number; layout: string }): Promise<{ slide: number; layout: string }> {
  return PowerPoint.run(async (ctx) => {
    const layout = await findLayout(ctx, args.layout);
    ctx.presentation.slides.getItemAt(args.slide - 1).applyLayout(layout);
    await ctx.sync();
    return { slide: args.slide, layout: layout.name };
  });
}

export async function getSlideImage(args: { slide: number; width?: number; height?: number }): Promise<{ slide: number; mediaType: string; base64: string }> {
  return PowerPoint.run(async (ctx) => {
    const image = ctx.presentation.slides.getItemAt(args.slide - 1).getImageAsBase64({
      width: args.width ?? 512,
      ...(args.height ? { height: args.height } : {}),
    });
    await ctx.sync();
    return { slide: args.slide, mediaType: "image/png", base64: image.value };
  });
}

export async function exportSlides(args: { slides: number[]; filename?: string }): Promise<{ downloaded: true; bytes: number; slides: number[] }> {
  const base64 = await PowerPoint.run(async (ctx) => {
    const collection = ctx.presentation.slides;
    collection.load("items/id");
    await ctx.sync();
    const selected = args.slides.map((number) => collection.getItemAt(number - 1));
    const result = collection.exportAsBase64Presentation(selected);
    await ctx.sync();
    return result.value;
  });
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  }));
  const link = document.createElement("a");
  link.href = url;
  link.download = args.filename ?? "pontifexx-export.pptx";
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { downloaded: true as const, bytes: bytes.length, slides: args.slides };
}

export async function importSlides(args: { base64: string; after_slide?: number; source_formatting?: boolean }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    let targetSlideId: string | undefined;
    if (args.after_slide) {
      const target = ctx.presentation.slides.getItemAt(args.after_slide - 1);
      target.load("id");
      await ctx.sync();
      targetSlideId = target.id;
    }
    ctx.presentation.insertSlidesFromBase64(args.base64, {
      ...(targetSlideId ? { targetSlideId } : {}),
      formatting: args.source_formatting === false ? "UseDestinationTheme" : "KeepSourceFormatting",
    });
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function addLine(args: { slide: number; left: number; top: number; width: number; height: number; connector_type?: "Straight" | "Elbow" | "Curve"; color?: string; weight?: number; dash_style?: PowerPoint.ShapeLineDashStyle }): Promise<{ slide: number; shapeId: string }> {
  return PowerPoint.run(async (ctx) => {
    const shape = ctx.presentation.slides.getItemAt(args.slide - 1).shapes.addLine(
      args.connector_type ?? "Straight",
      { left: args.left, top: args.top, width: args.width, height: args.height },
    );
    if (args.color) shape.lineFormat.color = args.color;
    if (args.weight !== undefined) shape.lineFormat.weight = args.weight;
    if (args.dash_style) shape.lineFormat.dashStyle = args.dash_style;
    shape.load("id");
    await ctx.sync();
    return { slide: args.slide, shapeId: shape.id };
  });
}

export async function addTable(args: { slide: number; values: string[][]; left?: number; top?: number; width?: number; height?: number; style?: PowerPoint.TableStyle }): Promise<{ slide: number; shapeId: string; rows: number; columns: number }> {
  if (!args.values.length || !args.values[0]?.length) throw new Error("Tabel vereist minstens één cel.");
  const columns = args.values[0].length;
  if (args.values.some((row) => row.length !== columns)) throw new Error("Alle tabelrijen moeten evenveel kolommen hebben.");
  return PowerPoint.run(async (ctx) => {
    const shape = ctx.presentation.slides.getItemAt(args.slide - 1).shapes.addTable(args.values.length, columns, {
      values: args.values,
      left: args.left,
      top: args.top,
      width: args.width,
      height: args.height,
      style: args.style,
    });
    shape.load("id");
    await ctx.sync();
    return { slide: args.slide, shapeId: shape.id, rows: args.values.length, columns };
  });
}

export async function readTable(args: { slide: number; shape: string }): Promise<{ values: string[][]; rows: number; columns: number }> {
  return PowerPoint.run(async (ctx) => {
    const shape = await findShape(ctx, args.slide, args.shape);
    const table = shape.getTable();
    table.load("values,rowCount,columnCount");
    await ctx.sync();
    return { values: table.values, rows: table.rowCount, columns: table.columnCount };
  });
}

export async function updateTableCell(args: { slide: number; shape: string; row: number; column: number; text?: string; fill_color?: string; font_color?: string; font_size?: number; bold?: boolean; align?: PowerPoint.ParagraphHorizontalAlignment }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const table = (await findShape(ctx, args.slide, args.shape)).getTable();
    const cell = table.getCellOrNullObject(args.row - 1, args.column - 1);
    if (args.text !== undefined) cell.text = args.text;
    if (args.fill_color) cell.fill.setSolidColor(args.fill_color);
    if (args.font_color) cell.font.color = args.font_color;
    if (args.font_size !== undefined) cell.font.size = args.font_size;
    if (args.bold !== undefined) cell.font.bold = args.bold;
    if (args.align) cell.horizontalAlignment = args.align;
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function groupShapes(args: { slide: number; shapes: string[] }): Promise<{ slide: number; shapeId: string }> {
  return PowerPoint.run(async (ctx) => {
    const found: PowerPoint.Shape[] = [];
    for (const ref of args.shapes) found.push(await findShape(ctx, args.slide, ref));
    const group = ctx.presentation.slides.getItemAt(args.slide - 1).shapes.addGroup(found);
    group.load("id");
    await ctx.sync();
    return { slide: args.slide, shapeId: group.id };
  });
}

export async function ungroupShapes(args: { slide: number; shape: string }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    (await findShape(ctx, args.slide, args.shape)).group.ungroup();
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function selectShapes(args: { slide: number; shapes: string[] }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const found: PowerPoint.Shape[] = [];
    for (const ref of args.shapes) found.push(await findShape(ctx, args.slide, ref));
    ctx.presentation.slides.getItemAt(args.slide - 1).setSelectedShapes(found.map((shape) => shape.id));
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function arrangeShapes(args: { slide: number; shapes: string[]; operation: "align_left" | "align_center" | "align_right" | "align_top" | "align_middle" | "align_bottom" | "distribute_horizontal" | "distribute_vertical" }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const shapes: PowerPoint.Shape[] = [];
    for (const ref of args.shapes) shapes.push(await findShape(ctx, args.slide, ref));
    shapes.forEach((shape) => shape.load("left,top,width,height"));
    await ctx.sync();
    if (shapes.length < 2) throw new Error("Minstens twee shapes vereist.");
    const left = Math.min(...shapes.map((s) => s.left));
    const right = Math.max(...shapes.map((s) => s.left + s.width));
    const top = Math.min(...shapes.map((s) => s.top));
    const bottom = Math.max(...shapes.map((s) => s.top + s.height));
    for (const shape of shapes) {
      if (args.operation === "align_left") shape.left = left;
      if (args.operation === "align_center") shape.left = (left + right - shape.width) / 2;
      if (args.operation === "align_right") shape.left = right - shape.width;
      if (args.operation === "align_top") shape.top = top;
      if (args.operation === "align_middle") shape.top = (top + bottom - shape.height) / 2;
      if (args.operation === "align_bottom") shape.top = bottom - shape.height;
    }
    if (args.operation === "distribute_horizontal") {
      const ordered = [...shapes].sort((a, b) => a.left - b.left);
      const total = ordered.reduce((sum, s) => sum + s.width, 0);
      const gap = (right - left - total) / (ordered.length - 1);
      let cursor = left;
      for (const shape of ordered) { shape.left = cursor; cursor += shape.width + gap; }
    }
    if (args.operation === "distribute_vertical") {
      const ordered = [...shapes].sort((a, b) => a.top - b.top);
      const total = ordered.reduce((sum, s) => sum + s.height, 0);
      const gap = (bottom - top - total) / (ordered.length - 1);
      let cursor = top;
      for (const shape of ordered) { shape.top = cursor; cursor += shape.height + gap; }
    }
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function setShapeZOrder(args: { slide: number; shape: string; position: PowerPoint.ShapeZOrder }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    (await findShape(ctx, args.slide, args.shape)).setZOrder(args.position);
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function setShapeMetadata(args: { slide: number; shape: string; name?: string; alt_title?: string; alt_description?: string; decorative?: boolean; visible?: boolean; rotation?: number }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const shape = await findShape(ctx, args.slide, args.shape);
    if (args.name !== undefined) shape.name = args.name;
    if (args.alt_title !== undefined) shape.altTextTitle = args.alt_title;
    if (args.alt_description !== undefined) shape.altTextDescription = args.alt_description;
    if (args.decorative !== undefined) shape.isDecorative = args.decorative;
    if (args.visible !== undefined) shape.visible = args.visible;
    if (args.rotation !== undefined) shape.rotation = args.rotation;
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function setShapeHyperlink(args: { slide: number; shape: string; address?: string; screen_tip?: string }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const shape = await findShape(ctx, args.slide, args.shape);
    shape.setHyperlink({ address: args.address, screenTip: args.screen_tip });
    await ctx.sync();
    return { ok: true as const };
  });
}

export async function addImage(args: { slide: number; base64: string; left?: number; top?: number; width?: number; height?: number }): Promise<{ slide: number; shapeId?: string }> {
  const before = await PowerPoint.run(async (ctx) => {
    const slide = ctx.presentation.slides.getItemAt(args.slide - 1);
    slide.load("id");
    slide.shapes.load("items/id");
    await ctx.sync();
    ctx.presentation.setSelectedSlides([slide.id]);
    await ctx.sync();
    return slide.shapes.items.map((shape) => shape.id);
  });
  await new Promise<void>((resolve, reject) => {
    Office.context.document.setSelectedDataAsync(
      args.base64.replace(/^data:image\/[^;]+;base64,/, ""),
      {
        coercionType: Office.CoercionType.Image,
        imageLeft: args.left,
        imageTop: args.top,
        imageWidth: args.width,
        imageHeight: args.height,
      },
      (result) => result.status === Office.AsyncResultStatus.Succeeded
        ? resolve()
        : reject(new Error(result.error?.message ?? "Afbeelding invoegen mislukt.")),
    );
  });
  return PowerPoint.run(async (ctx) => {
    const shapes = ctx.presentation.slides.getItemAt(args.slide - 1).shapes;
    shapes.load("items/id");
    await ctx.sync();
    const added = shapes.items.find((shape) => !before.includes(shape.id));
    return { slide: args.slide, ...(added ? { shapeId: added.id } : {}) };
  });
}

export async function setSlideBackground(args: { slide: number; color?: string; transparency?: number; reset?: boolean; hide_graphics?: boolean }): Promise<{ ok: true }> {
  return PowerPoint.run(async (ctx) => {
    const background = ctx.presentation.slides.getItemAt(args.slide - 1).background;
    if (args.reset) background.reset();
    else if (args.color) background.fill.setSolidFill({ color: args.color, transparency: args.transparency });
    if (args.hide_graphics !== undefined) background.areBackgroundGraphicsHidden = args.hide_graphics;
    await ctx.sync();
    return { ok: true as const };
  });
}

// ───── Power tool: arbitrary PowerPoint.js code ─────
//
// SECURITY: this lets the model execute arbitrary JavaScript in the taskpane
// against the active presentation. Only safe inside trusted internal use. The
// agent system prompt should still prefer the specific tools above and only
// reach for this when nothing else fits.
export async function runPowerPointScript(args: {
  code: string;
  description?: string;
}): Promise<{ result: unknown; description?: string }> {
  const body = args.code;
  const fn = new Function("context", "PowerPoint", `return (async () => {\n${body}\n})();`);
  return PowerPoint.run(async (ctx) => {
    const result = await fn(ctx, PowerPoint);
    return { result: result ?? null, description: args.description };
  });
}

// Subscribe to selection changes — used by the input chip preview.
export function onSelectionChanged(cb: (sel: SelectionPayload) => void): () => void {
  let cancelled = false;
  const handler = async () => {
    if (cancelled) return;
    try {
      cb(await readSelection());
    } catch {
      /* selectie onleesbaar — negeren */
    }
  };
  // Initial read
  handler();
  Office.context.document.addHandlerAsync(
    Office.EventType.DocumentSelectionChanged,
    handler,
  );
  return () => {
    cancelled = true;
    Office.context.document.removeHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      { handler },
    );
  };
}
